import { Base, toast } from '@itsrever/design-system'
import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { useMarkAsRefundedExternally } from '@/hooks/use-mark-as-refunded-externally'
import { processDetailsKey } from '@/application/queries'

const {
  DialogClose,
  DialogTitle,
  DialogDescription,
  Button,
  DialogHeader,
  DialogFooter
} = Base

interface MarkAsRefundedConfirmationProps {
  onClose: () => void
  processId: string
  executedAmount: string
  currency: string
  note: string
}

export const MarkAsRefundedConfirmation = ({
  onClose,
  processId,
  executedAmount,
  currency,
  note
}: MarkAsRefundedConfirmationProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { mutate: markAsRefunded, isPending: loading } =
    useMarkAsRefundedExternally()

  const handleConfirm = () => {
    markAsRefunded(
      {
        opspbMarkAsRefundedExternallyRequest: {
          process_id: processId,
          amount_override: {
            amount: executedAmount,
            currency_code: currency
          },
          note: note
        }
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            text: t('process_details_page.mark_as_refunded_success')
          })
          queryClient.invalidateQueries({
            queryKey: processDetailsKey(processId)
          })
          window.location.reload()
        },
        onError: () => {
          toast({
            variant: 'error',
            text: t('process_details_page.mark_as_refunded_error')
          })
          onClose()
        }
      }
    )
  }

  return (
    <div className="w-[28em] space-y-7">
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle>
            {t('process_details_page.confirm_external_refund_title')}
          </DialogTitle>
          <DialogDescription>
            {t('process_details_page.confirm_external_refund_description')}
          </DialogDescription>
        </DialogHeader>
        <dl className="divide-y-1 rounded-md border border-solid border-neutral-200 bg-neutral-50 p-4 text-sm">
          <div className="item-center flex justify-between gap-4 pb-4">
            <dt className="text-sm text-neutral-600">
              <Trans
                i18nKey="process_details_page.amount_executed_manually_label"
                values={{ currency }}
              />
            </dt>
            <dd className="text-sm">{executedAmount}</dd>
          </div>
          <div className="item-center flex flex-col justify-between gap-1 border-t border-solid border-neutral-200 pt-4">
            <dt className="text-sm text-neutral-600">
              {t('process_details_page.note_label')}
            </dt>
            <dd className="text-sm font-normal">{note}</dd>
          </div>
        </dl>
      </div>
      <DialogFooter>
        <DialogClose
          render={
            <Button variant="secondary" type="button" disabled={loading} />
          }
        >
          {t('process_details_page.cancel')}
        </DialogClose>
        <Button
          variant="primary"
          type="button"
          onClick={handleConfirm}
          loading={loading}
        >
          {t('process_details_page.confirm_external_refund_title')}
        </Button>
      </DialogFooter>
    </div>
  )
}
